import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import L from "leaflet";
import * as ReactDOMServer from "react-dom/server";

const isMobile =
  navigator.userAgentData?.mobile === true ||
  window.matchMedia("(pointer: coarse)").matches;
//const HIT_WIDTH = isMobile ? 25 : 12; // largura da faixa clicável
//const LINE_WIDTH = isMobile ? 6 : 7; // largura da linha visível min: 4
const VIEWER_HIT = false; // true para ver a faixa clicável

const drawConnection = (
  container,
  project,
  connection,
  connectionData,
  map,
  scale = 0,
  PopupContent,
  theme,
  rulerMode = false,
  onClick = () => {},
  isAnimationEnabled = true,
  editMode = false,
  variant,
  colorMode,
  lineWidth = 7,
) => {
  if (!connection?.positions || connection.positions.length < 2) return;
  //if (!connectionData.isDown) console.log(connectionData);
  // LIMPEZA COMPLETA: remove tanto a linha quanto o hitArea
  if (connection._pixiElements) {
    connection._pixiElements.forEach((element) => {
      if (element.destroyFn) element.destroyFn();
      if (container.children.includes(element)) {
        container.removeChild(element);
      }
    });
  }
  connection._pixiElements = [];

  //  const variant = connection.variant || "trafego";

  let color = PIXI.utils.string2hex(theme.palette.color.lineDefault);
  let points = [];
  let tickerFn;
  let line;
  const animation = editMode
    ? "desativada"
    : (connection?.config?.animation || "desativada").toLowerCase();

  if (editMode) {
    color = PIXI.utils.string2hex(theme.palette.color.lineDefault);
  } else if (connectionData?.isDown) {
    color = colorMode ? "#babdb7a1" : "#7c7b7bc2";
  } else if (variant === "trafego") {
    color = PIXI.utils.string2hex(connectionData?.corTraffic?.cor || "#808080");
  } else if (variant === "potencia") {
    color = PIXI.utils.string2hex(connectionData?.corPower?.cor || "#808080");
  }

  // converte lat/lng -> pontos de layer (absolutos no container)
  points = connection.positions.map((pos) => {
    const latLng = L.latLng(pos.lat, pos.long);
    const pt = project(latLng);
    return new PIXI.Point(pt.x, pt.y);
  });

  if (animation === "desativada" || connectionData?.isDown) {
    // linha simples sem shader
    line = createSimpleLine(points, color, scale, lineWidth);
  } else {
    // linha animada com shader
    line = createThickDashedLine(
      points,
      color,
      1,
      scale,
      connection.config.animation || "desativada",
      isAnimationEnabled,
      lineWidth,
    );
  }
  line.zIndex = 1;
  container.addChild(line);

  const hitArea = createHitAreaGraphics(
    points,
    scale,
    onClick,
    connection,
    rulerMode,
    lineWidth,
  );
  hitArea.zIndex = 2;
  container.addChild(hitArea);

  line.isConnection = true;
  hitArea.isConnection = true;

  // popup na linha
  let popup = L.tooltip([0, 0], {
    permanent: false,
    direction: "top",
    opacity: 0.9,
  }).setContent(
    ReactDOMServer.renderToString(
      <PopupContent
        variant={variant}
        connectionData={connectionData}
        host={connection.host}
        theme={theme}
      />,
    ),
  );
  // Deixa o evento somente no mouse, evitando ser disparado por toque
  if (!rulerMode) {
    hitArea.on("mousemove", (e) => {
      try {
        // preferir coords do DOM pointer (clientX/clientY) para map container conversion
        const orig = e?.data?.originalEvent || {};
        const clientX = orig.clientX ?? e.data.global.x;
        const clientY = orig.clientY ?? e.data.global.y;

        // rect do container do mapa para converter client -> containerPoint corretamente mesmo com resize/scroll
        const mapRect = map.getContainer().getBoundingClientRect();
        const containerPoint = L.point(
          clientX - mapRect.left,
          clientY - mapRect.top - 10,
        );
        const latlng = map.containerPointToLatLng(containerPoint);

        popup.setLatLng(latlng).openOn(map);
      } catch (err) {
        console.warn("popup pointermove error:", err);
      }
    });

    hitArea.on("mouseleave", () => {
      map.removeLayer(popup);
    });
  }
  //fim do popup

  // status DOWN piscando (mantive sua lógica)
  if (connectionData?.isDown && !editMode) {
    let isBright = false;
    const interval = setInterval(() => {
      line.tint = isBright ? 0xff0000 : color;
      isBright = !isBright;
    }, 400);
    line._interval = interval;
  }

  line.destroyFn = () => {
    try {
      const g = line;

      // Remove ticker com segurança
      if (tickerFn) {
        try {
          PIXI.Ticker.shared.remove(tickerFn);
        } catch (e) {}
        tickerFn = null;
      }

      // Remove piscada DOWN
      if (g && g._interval) {
        try {
          clearInterval(g._interval);
        } catch (e) {}
      }

      // Remove popup se ainda estiver no mapa
      try {
        if (popup && map && map.hasLayer && map.hasLayer(popup))
          map.removeLayer(popup);
      } catch (e) {}

      // Remove listeners do hitArea
      try {
        if (hitArea && typeof hitArea.removeAllListeners === "function")
          hitArea.removeAllListeners();
      } catch (e) {}
    } catch (err) {
      console.warn("Erro ao destruir linha:", err);
    }
  };
  connection._pixiElements.push(line);
  connection._pixiElements.push(hitArea);
};

// Cria uma linha animada usando shader
function createThickDashedLine(
  points,
  color = 0x00ff00,
  speed = 1,
  scale = 1,
  animation,
  isAnimationEnabled,
  lineWidth,
) {
  if (points.length < 2) return null;
  const DASH_PATTERN = {
    pontilhada: [lineWidth * 1.5, lineWidth * 2],
    tracejada: [lineWidth * 3, lineWidth * 2],
  }; //  linha, espaço

  const baseX = points[0].x;
  const baseY = points[0].y;
  const localPoints = points.map((p) => [
    (p.x - baseX) * scale,
    (p.y - baseY) * scale,
  ]);

  const verts = [];
  const distAttr = [];
  let totalDist = 0;

  for (let i = 0; i < localPoints.length - 1; i++) {
    const [x1, y1] = localPoints[i];
    const [x2, y2] = localPoints[i + 1];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);

    const nx = (-dy / len) * (lineWidth / 1.8);
    const ny = (dx / len) * (lineWidth / 1.8);

    verts.push(x1 + nx, y1 + ny, x1 - nx, y1 - ny);
    distAttr.push(totalDist, totalDist);

    totalDist += len;
    verts.push(x2 + nx, y2 + ny, x2 - nx, y2 - ny);
    distAttr.push(totalDist, totalDist);
  }

  const geometry = new PIXI.Geometry()
    .addAttribute("aVertexPosition", new Float32Array(verts), 2)
    .addAttribute("aDist", new Float32Array(distAttr), 1);

  const vertexSrc = `
    precision mediump float;
    attribute vec2 aVertexPosition;
    attribute float aDist;
    uniform mat3 translationMatrix;
    uniform mat3 projectionMatrix;
    varying float vDist;
    void main() {
      vDist = aDist;
      gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
    }
  `;

  const fragmentSrc = `
    precision mediump float;
    varying float vDist;
    uniform float uTime;
    uniform vec3 uColor;
    uniform float uDash;
    uniform float uGap;
    uniform float uSpeed;
    void main() {
      float total = uDash + uGap;
      float phase = mod(vDist - uTime * uSpeed, total);
      if(phase < uDash){
        gl_FragColor = vec4(uColor, 1.0);
      } else {
        discard;
      }
    }
  `;

  const shader = PIXI.Shader.from(vertexSrc, fragmentSrc, {
    uTime: 0,
    uColor: [
      ((color >> 16) & 0xff) / 255,
      ((color >> 8) & 0xff) / 255,
      (color & 0xff) / 255,
    ],
    uDash: DASH_PATTERN[animation]?.[0] || 20, // comprimento do traço
    uGap: DASH_PATTERN[animation]?.[1] || 20, // comprimento do espaço
    uSpeed: speed,
  });

  const mesh = new PIXI.Mesh(geometry, shader);
  mesh.drawMode = PIXI.DRAW_MODES.TRIANGLE_STRIP;
  mesh.position.set(baseX, baseY);
  mesh.state = PIXI.State.for2d();
  mesh.eventMode = "none";

  if (isAnimationEnabled)
    PIXI.Ticker.shared.add((delta) => {
      shader.uniforms.uTime += 1 * delta;
    });

  return mesh;
}
function createHitAreaGraphics(
  points,
  scale = 1,
  onClick = () => {},
  connection,
  rulerMode = false,
  lineWidth,
) {
  const hitArea = new PIXI.Graphics();
  hitArea.position.set(points[0].x, points[0].y);
  hitArea.eventMode = "static";
  hitArea.cursor = "pointer";
  hitArea.beginFill(0x00ff00, 0.25);
  if (!VIEWER_HIT) hitArea.alpha = 0; // remove a cor do hitArea

  // desenha cada segmento em coordenadas RELATIVAS À LINHA (subtraímos points[0])
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    // coordenadas locais relativas a points[0]
    const x1 = (p1.x - points[0].x) * scale;
    const y1 = (p1.y - points[0].y) * scale;
    const x2 = (p2.x - points[0].x) * scale;
    const y2 = (p2.y - points[0].y) * scale;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    let hitWidth = lineWidth > 12 ? lineWidth : 12; // aumenta a faixa clicável para linhas mais grossas
    if (isMobile && hitWidth < 25) hitWidth = 25; // aumenta ainda mais p
    const nx = (dy / len) * (hitWidth / 2);
    const ny = (-dx / len) * (hitWidth / 2);

    hitArea.moveTo(x1 - nx, y1 - ny);
    hitArea.lineTo(x2 - nx, y2 - ny);
    hitArea.lineTo(x2 + nx, y2 + ny);
    hitArea.lineTo(x1 + nx, y1 + ny);
    hitArea.closePath();
  }
  hitArea.endFill();
  hitArea.interactive = true;
  if (rulerMode) return hitArea;
  hitArea.on("pointerdown", (e) => {
    e.stopPropagation();
    onClick(connection.id);
  });
  return hitArea;
}
// Cria uma linha simples sem shader
function createSimpleLine(points, color = 0x00ff00, scale = 1, lineWidth) {
  if (points.length < 2) return null;
  const line = new PIXI.Graphics();
  line.position.set(points[0].x, points[0].y);
  line.lineStyle(lineWidth, color, 1);
  line.moveTo(0, 0);
  line.eventMode = "none";

  for (let i = 0; i < points.length; i++) {
    const relX = points[i].x - points[0].x;
    const relY = points[i].y - points[0].y;
    line.lineTo(relX * (i === 0 ? 1 : scale), relY * (i === 0 ? 1 : scale));
  }
  return line;
}

const scales = {
  3: 0.008,
  4: 0.0175,
  5: 0.0353,
  6: 0.071,
  7: 0.142,
  8: 0.284,
  9: 0.568,
  10: 1.1365,
  11: 2.2726,
  12: 4.5454,
  13: 9.091,
  14: 18.182,
  15: 36.3637,
  16: 72.7267,
  17: 145.454,
  18: 290.909,
  19: 581.818,
};

export { drawConnection, scales };
